import { useCallback, useEffect, useMemo, useState } from "react";
import Details from "./detail";
import Form from "./form";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { schema, type NewUserFormData } from "@/schemas/users/new-user";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import { useNavigate, useParams } from "react-router";
import { useHeaderButton } from "@/contexts/header-button-context";
import {
  Avatar,
  AvatarFallback,
  Button,
  Icon,
  useToast,
} from "@elaniintc/goes-design-system";
import {
  useActivateUser,
  useGetUser,
  useInactivateUser,
  useResetUserPassword,
  useUnblockUser,
  useUpdateUser,
} from "@/queries/users/users.api";
import { cn } from "@/utils/theme";
import type { AxiosError } from "axios";
import ResetPasswordModal from "./reset-password-modal";
import UnblockUserModal from "./unblock-user-modal";

const Edit = () => {
  const params = useParams();
  const { hasPermission } = useAuthorization();
  const canEditUser = hasPermission(Permissions.users.update);
  const [isUserActive, setIsUserActive] = useState(true);

  const { mutateAsync, isPending: isLoadingUpdate } = useUpdateUser(
    params.id ?? "",
  );
  const {
    mutateAsync: mutateToInactiveUser,
    isPending: isLoadingInactivateUser,
  } = useInactivateUser(params.id ?? "");
  const { mutateAsync: mutateToActiveUser, isPending: isLoadingActivateUser } =
    useActivateUser(params.id ?? "");

  const { data: user } = useGetUser(params.id ?? "");
  const userActivationHasChanged = isUserActive !== !!user?.is_active;

  const methods = useForm<NewUserFormData>({
    mode: "onChange",
    resolver: zodResolver(schema),
    defaultValues: {
      name: user?.name || "",
      last_name: user?.last_name || "",
      email: user?.email || "",
      phone_number: user?.phone_number || {
        number: "",
        calling_code: "503",
        country: "SV",
      },
      dui: user?.dui || "",
      assigned_institution_id: user?.institution?.id || "",
      department_id: user?.department?.id || "",
      registered_position_id: user?.position?.id || "",
      role_id: user?.user_roles?.[0]?.role_id || "",
    },
  });

  const [selectedRole, setSelectedRole] = useState<{
    label: string;
    type: string;
  } | null>(() => {
    const role = user?.user_roles?.[0];
    return role ? { label: role.role_name, type: role.role_type || "" } : null;
  });
  const [selectedPosition, setSelectedPosition] = useState<{
    label: string;
    value: string;
  } | null>(() => {
    const position = user?.position;
    return position
      ? { label: position?.name || position?.nombre || "", value: position.id }
      : null;
  });
  const [selectedUnit, setSelectedUnit] = useState<{
    label: string;
    value: string;
  } | null>(() => {
    const department = user?.department;
    return department ? { label: department.name, value: department.id } : null;
  });
  const [selectedOrganization, setSelectedOrganization] = useState<{
    label: string;
    value: string;
  } | null>(() => {
    const institution = user?.institution;
    return institution
      ? {
          label: institution?.name || institution?.nombre || "",
          value: institution.id,
        }
      : null;
  });

  useEffect(() => {
    methods.reset({
      name: user?.name || "",
      last_name: user?.last_name || "",
      email: user?.email || "",
      phone_number: user?.phone_number || {
        number: "",
        calling_code: "503",
        country: "SV",
      },
      dui: user?.dui || "",
      assigned_institution_id: user?.institution?.id || "",
      department_id: user?.department?.id || "",
      registered_position_id: user?.position?.id || "",
      role_id: user?.user_roles?.[0]?.role_id || "",
    });
    setSelectedRole(() => {
      const role = user?.user_roles?.[0];
      return role
        ? { label: role.role_name, type: role.role_type || "" }
        : null;
    });
    setSelectedPosition(() => {
      const position = user?.position;
      return position
        ? {
            label: position.name || position.nombre || "",
            value: position.id,
          }
        : null;
    });
    setSelectedUnit(() => {
      const department = user?.department;
      return department
        ? { label: department.name, value: department.id }
        : null;
    });
    setSelectedOrganization(() => {
      const institution = user?.institution;
      return institution
        ? {
            label: institution.name || institution.nombre || "",
            value: institution.id,
          }
        : null;
    });
    setIsUserActive(
      !!user?.is_active &&
        !user?.is_blocked &&
        user?.user_status !== "Bloqueo temporal",
    );
  }, [user, methods]);

  const navigate = useNavigate();
  const { setComp } = useHeaderButton();

  const dataHasChanged = useCallback(
    (currentData: NewUserFormData) => {
      return (
        currentData.name !== user?.name ||
        currentData.last_name !== user?.last_name ||
        currentData.email !== user?.email ||
        currentData.phone_number.number !== user?.phone_number?.number ||
        currentData.dui !== user?.dui ||
        currentData.assigned_institution_id !== user?.institution?.id ||
        currentData.department_id !== user?.department?.id ||
        currentData.registered_position_id !== user?.position?.id ||
        currentData.role_id !== user?.user_roles?.[0]?.role_id
      );
    },
    [user],
  );

  const data = methods.watch();
  const isButtonDisabled = useMemo(() => {
    return !dataHasChanged(data) && !userActivationHasChanged;
  }, [data, userActivationHasChanged, dataHasChanged]);

  useEffect(() => {
    setComp(
      <>
        <Button
          variant="quiet"
          size="xs"
          type="button"
          onClick={() => navigate(-1)}
        >
          Cancelar
          <Icon type="far" name="times" />
        </Button>
        <Button
          variant="default"
          size="xs"
          form="new-user-form"
          type="submit"
          disabled={
            isButtonDisabled ||
            isLoadingUpdate ||
            isLoadingActivateUser ||
            isLoadingInactivateUser
          }
        >
          Guardar cambios
          <Icon
            type="fas"
            name={
              isLoadingUpdate ||
              isLoadingActivateUser ||
              isLoadingActivateUser ||
              isLoadingInactivateUser
                ? "spinner-third"
                : "save"
            }
            className={cn(
              "text-current",
              (isLoadingUpdate ||
                isLoadingActivateUser ||
                isLoadingInactivateUser) &&
                "animate-spin",
            )}
          />
        </Button>
      </>,
    );
    return () => {
      setComp(null);
    };
  }, [
    navigate,
    setComp,
    isLoadingUpdate,
    isButtonDisabled,
    isLoadingActivateUser,
    isLoadingInactivateUser,
  ]);

  const { showToast } = useToast();

  const applyMutationsAndNotify = async (
    data: NewUserFormData,
    description: string,
  ) => {
    const needsUpdate = dataHasChanged(data);
    if (userActivationHasChanged && isUserActive) {
      await mutateToActiveUser({ description });
      if (needsUpdate) await mutateAsync(data);
      showToast({
        title: "Usuario activado",
        description: "El usuario ha sido activado correctamente.",
        options: { type: "success", duration: 4000 },
      });
    } else if (userActivationHasChanged) {
      if (needsUpdate) await mutateAsync(data);
      await mutateToInactiveUser({ description });
      showToast({
        title: "Usuario desactivado",
        description: "El usuario ha sido desactivado exitosamente.",
        options: {
          type: "success",
          duration: 5000,
          customIcon: (
            <Avatar size="xs" shape="square">
              <AvatarFallback color="danger" shape="square">
                <Icon type="fas" name="user-minus" />
              </AvatarFallback>
            </Avatar>
          ),
        },
      });
    } else {
      if (needsUpdate) await mutateAsync(data);
      showToast({
        title: "Usuario actualizado",
        description: "El usuario ha sido actualizado correctamente.",
        options: { type: "success", duration: 4000 },
      });
    }
  };

  const onSubmit = async (data: NewUserFormData, description: string) => {
    try {
      await applyMutationsAndNotify(data, description);
      navigate("/usuarios");
    } catch (e) {
      const error = e as AxiosError<{
        message: string;
        errors?: { [key: string]: string[] };
      }>;
      if (error.response?.status === 422) {
        const errors = error.response.data.errors;
        if (errors?.email) {
          methods.setError("email", {
            type: "manual",
            message: errors.email[0] || "",
          });
        }
        if (errors?.dui) {
          methods.setError("dui", {
            type: "manual",
            message: errors.dui[0] || "",
          });
        }
      }
      showToast({
        title: "Error al actualizar el usuario",
        description: "Revisa los datos ingresados e inténtalo de nuevo.",
        options: { type: "error", duration: 4000 },
      });
    }
  };

  const { mutateAsync: mutateUnblockUser, isPending: isLoadingUnblockUser } =
    useUnblockUser(params.id ?? "");

  const {
    mutateAsync: mutateResetPassword,
    isPending: isLoadingResetPassword,
  } = useResetUserPassword(params.id ?? "");

  const [modalState, setModalState] = useState<
    "reset-password" | "idle" | "unblock"
  >("idle");
  const onResetPassword = async () => {
    try {
      await mutateResetPassword();
      showToast({
        title: "Cambio de contraseña solicitado",
        description:
          "El usuario recibirá una solicitud para completar el proceso de restablecimiento de contraseña.",
        options: {
          type: "success",
          duration: 5000,
          customIcon: (
            <Avatar size="xs" shape="square">
              <AvatarFallback color="action" shape="square">
                <Icon type="fas" name="key" />
              </AvatarFallback>
            </Avatar>
          ),
        },
      });
    } catch (e) {
      const error = e as AxiosError<{ message: string }>;
      console.error("Error resetting password:", error);
      showToast({
        title: "Error al intentar restablecer contraseña",
        description:
          "La solicitud de restablecimiento de contraseña no pudo ser enviado, inténtalo de nuevo.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
    }
    setModalState("idle");
  };

  const onUnblockUser = async () => {
    try {
      await mutateUnblockUser();
      showToast({
        title: "Usuario desbloqueado",
        description:
          "El usuario fué desbloqueado, ahora puede volver a iniciar sesión y utilizar la plataforma.",
        options: {
          type: "success",
          duration: 5000,
          customAction: (
            <Avatar size="xs" shape="square">
              <AvatarFallback color="success" shape="square">
                <Icon type="fas" name="unlock" />
              </AvatarFallback>
            </Avatar>
          ),
        },
      });
      setIsUserActive(true);
    } catch (e) {
      const error = e as AxiosError<{ message: string }>;
      console.error("Error unlocking user:", error);
      showToast({
        title: "Error al intentar desbloquear usuario",
        description:
          "El usuario no pudo ser desbloqueado. Por favor, inténtalo de nuevo.",
        options: {
          type: "error",
          duration: 4000,
        },
      });
    }
    setModalState("idle");
  };

  return (
    <div className="grid grid-cols-1 tablet:grid-cols-2 gap-3">
      {modalState === "reset-password" && (
        <ResetPasswordModal
          isOpen={modalState === "reset-password"}
          isLoading={isLoadingResetPassword}
          onClose={() => setModalState("idle")}
          onAccept={onResetPassword}
        />
      )}
      {modalState === "unblock" && (
        <UnblockUserModal
          isOpen={modalState === "unblock"}
          isLoading={isLoadingUnblockUser}
          onClose={() => setModalState("idle")}
          onAccept={onUnblockUser}
        />
      )}
      <FormProvider {...methods}>
        <Details
          selectedPosition={selectedPosition}
          selectedOrganization={selectedOrganization}
          selectedUnit={selectedUnit}
          selectedRole={selectedRole}
          isUserActive={isUserActive}
          toggleUserActivation={setIsUserActive}
          isUserBlocked={
            user?.is_blocked || user?.user_status === "Bloqueo temporal"
          }
          onResetPassword={() => {
            setModalState("reset-password");
          }}
          onUnblockUser={() => {
            setModalState("unblock");
          }}
        />
        {canEditUser && (
          <Form
            isUserDeactivated={userActivationHasChanged && !isUserActive}
            isUserActivated={userActivationHasChanged && isUserActive}
            setSelectedRole={setSelectedRole}
            setSelectedUnit={setSelectedUnit}
            setSelectedOrganization={setSelectedOrganization}
            setSelectedPosition={setSelectedPosition}
            selectedOrganization={selectedOrganization}
            selectedPosition={selectedPosition}
            onSubmit={onSubmit}
          />
        )}
      </FormProvider>
    </div>
  );
};

export default Edit;
